// ── Compresión y descompresión binaria de acelerómetro (X, Y, Z @ 50 Hz en mili-G) ──

export async function packAndCompressAcc(accObj) {
  const { x, y, z, startTime = Date.now(), fs = 50 } = accObj
  const N = Math.min(x.length, y.length, z.length)
  if (N === 0) return null

  // Encabezado de 16 bytes:
  // [0..3]:   'ACC1' (0x41434331)
  // [4..7]:   sampleCount N (Uint32)
  // [8..9]:   fs (Uint16)
  // [10..13]: startTime seg (Uint32)
  // [14..15]: startTime ms (Uint16)
  const headerBytes = 16
  const sampleBytes = N * 6
  const totalRawBytes = headerBytes + sampleBytes
  const rawBuf = new ArrayBuffer(totalRawBytes)
  const view = new DataView(rawBuf)

  view.setUint32(0, 0x41434331, false)
  view.setUint32(4, N, true)
  view.setUint16(8, fs, true)
  view.setUint32(10, Math.floor(startTime / 1000), true)
  view.setUint16(14, startTime % 1000, true)

  let offset = 16
  for (let i = 0; i < N; i++) {
    view.setInt16(offset, Math.max(-32768, Math.min(32767, Math.round(x[i]))), true)
    view.setInt16(offset + 2, Math.max(-32768, Math.min(32767, Math.round(y[i]))), true)
    view.setInt16(offset + 4, Math.max(-32768, Math.min(32767, Math.round(z[i]))), true)
    offset += 6
  }

  const rawBytes = new Uint8Array(rawBuf)

  let compressedBytes = rawBytes
  if (typeof CompressionStream !== 'undefined') {
    try {
      const cs = new CompressionStream('gzip')
      const writer = cs.writable.getWriter()
      writer.write(rawBytes)
      writer.close()
      const chunks = []
      const reader = cs.readable.getReader()
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        chunks.push(value)
      }
      const totalLen = chunks.reduce((acc, c) => acc + c.length, 0)
      const merged = new Uint8Array(totalLen)
      let pos = 0
      for (const c of chunks) {
        merged.set(c, pos)
        pos += c.length
      }
      compressedBytes = merged
    } catch (e) {
      console.warn('CompressionStream failed, using raw buffer:', e)
    }
  }

  let binary = ''
  const len = compressedBytes.byteLength
  for (let i = 0; i < len; i += 8192) {
    binary += String.fromCharCode.apply(null, compressedBytes.subarray(i, Math.min(i + 8192, len)))
  }
  const base64 = btoa(binary)

  return {
    sampleCount: N,
    fs,
    startTime,
    rawBytesCount: totalRawBytes,
    compressedBytesCount: compressedBytes.length,
    ratio: (compressedBytes.length / totalRawBytes).toFixed(2),
    dataBase64: base64,
  }
}

export async function unpackAndDecompressAcc(dataBase64) {
  const binary = atob(dataBase64)
  const len = binary.length
  const compressedBytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) compressedBytes[i] = binary.charCodeAt(i)

  let rawBytes = compressedBytes
  if (compressedBytes[0] === 0x1F && compressedBytes[1] === 0x8B && typeof DecompressionStream !== 'undefined') {
    const ds = new DecompressionStream('gzip')
    const writer = ds.writable.getWriter()
    writer.write(compressedBytes)
    writer.close()
    const chunks = []
    const reader = ds.readable.getReader()
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      chunks.push(value)
    }
    const totalLen = chunks.reduce((acc, c) => acc + c.length, 0)
    const merged = new Uint8Array(totalLen)
    let pos = 0
    for (const c of chunks) {
      merged.set(c, pos)
      pos += c.length
    }
    rawBytes = merged
  }

  const view = new DataView(rawBytes.buffer, rawBytes.byteOffset, rawBytes.byteLength)
  const magic = view.getUint32(0, false)
  if (magic !== 0x41434331) throw new Error('Formato ACC inválido')

  const sampleCount = view.getUint32(4, true)
  const fs = view.getUint16(8, true)
  const startSec = view.getUint32(10, true)
  const startMs = view.getUint16(14, true)
  const startTime = startSec * 1000 + startMs

  const x = new Int16Array(sampleCount)
  const y = new Int16Array(sampleCount)
  const z = new Int16Array(sampleCount)

  let offset = 16
  for (let i = 0; i < sampleCount; i++) {
    x[i] = view.getInt16(offset, true)
    y[i] = view.getInt16(offset + 2, true)
    z[i] = view.getInt16(offset + 4, true)
    offset += 6
  }

  return { x, y, z, sampleCount, fs, startTime }
}

export function exportAccToCsv(accData) {
  const { x, y, z, sampleCount, fs, startTime } = accData
  const dtMs = 1000 / fs
  const lines = ['timestamp_iso,elapsed_ms,x_mg,y_mg,z_mg,magnitude_mg']
  for (let i = 0; i < sampleCount; i++) {
    const t = startTime + Math.round(i * dtMs)
    const iso = new Date(t).toISOString()
    const elapsed = Math.round(i * dtMs)
    const mag = Math.round(Math.sqrt(x[i] * x[i] + y[i] * y[i] + z[i] * z[i]))
    lines.push(`${iso},${elapsed},${x[i]},${y[i]},${z[i]},${mag}`)
  }
  return lines.join('\n')
}
